import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useArtistSearch } from '../hooks/useArtistSearch';
import { profileMessages as messages } from './profileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistSearchProps {
  /** The artists already in the profile: their rows show no Add. */
  known: ReadonlySet<number>;
  onAdd: (musicapiArtistId: number) => void;
}

/** "Search an artist you sound like" (design outreach b3): the Browse artist index. */
const ArtistSearchComponent: React.FC<ArtistSearchProps> = ({ known, onAdd }) => {
  const intl = useIntl();
  const [text, setText] = useState('');
  const { hits, isLoading } = useArtistSearch(text);
  const rows = useMemo(
    () =>
      hits
        .filter((hit) => !known.has(hit.musicapiArtistId))
        .map((hit) => (
          <div key={hit.musicapiArtistId} className={styles.miniRow} data-testid="artist-search-hit">
            {hit.imageUrl !== null ? (
              <img className={styles.cover} src={hit.imageUrl} alt="" />
            ) : (
              <span className={styles.cover} />
            )}
            <div className={styles.grow}>
              <b>{hit.name}</b>
            </div>
            <SMLButton
              size="sm"
              outline
              omitCap
              onClick={() => {
                onAdd(hit.musicapiArtistId);
                setText('');
              }}
            >
              {intl.formatMessage(messages.add)}
            </SMLButton>
          </div>
        )),
    [hits, intl, known, onAdd]
  );

  return (
    <div className={styles.stack}>
      <input
        className={styles.input}
        value={text}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        aria-label={intl.formatMessage(messages.searchPlaceholder)}
        onChange={(event) => setText(event.target.value)}
      />
      {text.trim() !== '' && !isLoading && rows.length === 0 && (
        <p className={styles.small}>{intl.formatMessage(messages.noHits)}</p>
      )}
      <div className={styles.miniList}>{rows}</div>
    </div>
  );
};

export const ArtistSearch = TypedMemo(ArtistSearchComponent);
